import * as AsserThat from '~/utils/AssertThat';

export function formatDateUTC(
  dateInput: string | number | Date | undefined,
  format: 'MMM DD, YYYY' | 'MM/DD/YYYY' | 'YYYY.MM.DD' | string = 'MMM DD, YYYY'
): string {
  if (!dateInput) {
    throw new Error(`Invalid date input: ${dateInput}`);
  }

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date input: ${dateInput}`);
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).formatToParts(date);

  const map: Record<string, string> = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  const replacements: Record<string, string> = {
    MMM: map.month ?? '', // e.g., "Apr"
    MM: String(date.getUTCMonth() + 1).padStart(2, '0'),
    DD: map.day ?? '',
    YYYY: map.year ?? '',
  };

  // Replace longer tokens first to avoid MM/M conflict
  return format.replace(/YYYY|MMM|MM|DD/g, (match: string) => replacements[match] || match);
}

export function normalizeDateToISOString(dateInput?: string): string | undefined {
  if (!dateInput) {
    return undefined;
  }

  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date.toISOString();
}

export function formatLocalDateWithZone(
  dateInput: string | undefined,
  format: 'MMM DD, YYYY' | 'MM/DD/YYYY' | 'YYYY.MM.DD' | string = 'DD MMM YYYY, HH:mm z (X, City)',
  timeZoneInput?: string
): string {
  dateInput = AsserThat.getNonNullOrThrow(dateInput, 'dateInput cannot be null or undefined');

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    console.error(`Invalid date input: ${dateInput}`);
    return '';
  }

  const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let timeZone = timeZoneInput ?? fallbackTimeZone;

  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(date);
  } catch (err) {
    console.warn('could not find time zone:', err);
    timeZone = fallbackTimeZone;
  }

  const offsetPart = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'shortOffset',
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;
  const utcOffset = (offsetPart ?? 'GMT').replace('GMT', 'UTC');

  // Format date using locale-aware formatter
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(date);

  const map: Record<string, string> = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  const city = timeZone.split('/')[1]?.replace('_', ' ') || timeZone;
  const replacements: Record<string, string> = {
    MMM: map.month ?? '', // e.g. Apr
    MM: String(date.getMonth() + 1).padStart(2, '0'),
    DD: map.day ?? '',
    YYYY: map.year ?? '',
    HH: map.hour ?? '',
    mm: map.minute ?? '',
    z: map.timeZoneName ?? '',
    X: utcOffset,
    City: city,
  };

  const formattedDate = format?.replace(
    /YYYY|MMM|MM|DD|HH|mm|z|X|City/g,
    (match) => replacements[match] || match
  );

  return formattedDate;
}

export const formatEventDateRange = (start: string, end: string | null): string => {
  const formattedStart = formatDateUTC(start, 'MMM DD');
  if (!end) return formattedStart;
  const formattedEnd = formatDateUTC(end, 'MMM DD');
  if (formattedStart === formattedEnd) return formattedStart;
  const sameMonth = formatDateUTC(start, 'MMM') === formatDateUTC(end, 'MMM');
  if (sameMonth) return `${formattedStart}–${formatDateUTC(end, 'DD')}`;
  return `${formattedStart} – ${formattedEnd}`;
};
